import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import DescriptionIcon from '@mui/icons-material/Description';
import { supabase } from '../../config/supabase';

// Use shared Supabase client from config

const MED_NEUTRAL_CARD_BG = 'linear-gradient(160deg, rgba(108, 162, 228, 0.13), rgba(60, 106, 176, 0.1))';
const MED_NEUTRAL_CARD_BORDER = 'none';
const MED_ACCENT_CARD_BG = 'linear-gradient(160deg, rgba(60, 146, 228, 0.18), rgba(26, 94, 168, 0.14))';
const MED_ACCENT_CARD_BORDER = 'none';
const MED_ACCENT_CHIP_BG = 'rgba(64, 150, 230, 0.18)';
const MED_ACCENT_TEXT = '#57e8ff';
const MED_PANEL_BG = 'linear-gradient(165deg, rgba(10, 44, 98, 0.34), rgba(7, 32, 74, 0.3))';
const MED_PANEL_BORDER = 'none';
const POPUP_RADIUS = '8px';

const MedicalFileDialog = ({ open, onClose, emiratesId, patientName }) => {
  const [loading, setLoading] = useState(false);
  const [medicalRecords, setMedicalRecords] = useState([]);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState({ allergies: [], chronicDiseases: [], text: '' });

  const fetchMedicalRecords = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { data, error: fetchError } = await supabase
        .from('AiDoctor')
        .select('*')
        .eq('emiratesId', emiratesId)
        .order('appointmentDate', { ascending: false });

      if (fetchError) throw fetchError;

      setMedicalRecords(data || []);
      
      // Generate summary from records
      if (data && data.length > 0) {
        generateSummary(data);
      }
    } catch (err) {
      console.error('Error fetching medical records:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [emiratesId]);

  useEffect(() => {
    if (open && emiratesId) {
      fetchMedicalRecords();
    }
  }, [open, emiratesId, fetchMedicalRecords]);

  const generateSummary = (records) => {
    // Extract all allergies
    const allergiesSet = new Set();
    records.forEach(record => {
      if (record.allergies && record.allergies !== 'NULL' && record.allergies !== 'None') {
        const allergyList = record.allergies.split(',').map(a => a.trim());
        allergyList.forEach(allergy => allergiesSet.add(allergy));
      }
    });

    // Extract chronic diseases from visit reasons, medications, and reports
    const chronicKeywords = ['diabetes', 'hypertension', 'asthma', 'copd', 'heart disease', 
      'arthritis', 'chronic kidney', 'chronic pain', 'thyroid', 'cancer', 'epilepsy'];
    const chronicDiseasesSet = new Set();
    
    records.forEach(record => {
      const textToCheck = `${record.visitReason || ''} ${record.medications || ''} ${record.reports || ''}`.toLowerCase();
      chronicKeywords.forEach(keyword => {
        if (textToCheck.includes(keyword)) {
          // Capitalize first letter
          chronicDiseasesSet.add(keyword.charAt(0).toUpperCase() + keyword.slice(1));
        }
      });
    });

    // Generate summary text
    let summaryText = '';
    
    const recentVisits = records.slice(0, 3);
    const specialties = [...new Set(recentVisits.map(r => r.doctorSpecialty).filter(Boolean))];
    if (specialties.length > 0) {
      summaryText += `Recent visits include ${specialties.join(', ')}. `;
    }

    if (chronicDiseasesSet.size > 0) {
      summaryText += `Patient is being monitored for ${Array.from(chronicDiseasesSet).join(', ')}. `;
    }

    setSummary({
      allergies: Array.from(allergiesSet),
      chronicDiseases: Array.from(chronicDiseasesSet),
      text: summaryText
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRecordTypeColor = (type) => {
    const colors = {
      diagnosis: '#ff8f8a',
      consultation: '#edfff1',
      prescription: '#95E1D3',
      lab_result: '#F38181',
      default: '#c9d6e1'
    };
    return colors[type] || colors.default;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: 'linear-gradient(160deg, rgba(6, 32, 84, 0.38), rgba(4, 24, 66, 0.34))',
          backdropFilter: 'blur(20px)',
          border: 'none',
          boxShadow: '0 18px 44px rgba(2, 14, 38, 0.38), 0 0 18px rgba(38, 135, 230, 0.14)',
          borderRadius: POPUP_RADIUS,
          maxHeight: '90vh',
        }
      }}
    >
      <DialogContent
        sx={{
          mt: 0,
          p: { xs: 2, sm: 2.5 },
          background: 'linear-gradient(180deg, rgba(8, 33, 76, 0.26), rgba(4, 22, 58, 0.24))',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <MedicalServicesIcon sx={{ color: '#c9d6e1', fontSize: '20pt' }} />
            <Box>
              {patientName && (
                <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, mb: 0.2 }}>
                  {patientName} • {emiratesId}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: '#d9e9f7',
              border: 'none',
              borderRadius: POPUP_RADIUS,
              background: 'rgba(8, 38, 84, 0.35)',
              '&:hover': { background: 'rgba(61, 143, 227, 0.18)' },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <CircularProgress sx={{ color: '#c9d6e1' }} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && medicalRecords.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <DescriptionIcon sx={{ fontSize: 64, color: 'rgba(223, 249, 255, 0.3)', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#fff', mb: 1 }}>
              No Medical Records Found
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
              There are no medical records associated with this Emirates ID yet.
            </Typography>
          </Box>
        )}

        {!loading && !error && medicalRecords.length > 0 && (
          <>
            {/* Summary */}
            <Box sx={{ mb: 3 }}>
              <Typography  sx={{ color: '#c9d6e1', mb: 1,fontSize:"18pt",fontWeight:500,fontFamily:"Dubai"  }}>
                Summary
              </Typography>
              
              {/* Summary Text */}
              {summary.text && (
                <Box sx={{ 
                  mb: 2, 
                  p: 2, 
                  background: MED_PANEL_BG,
                  borderRadius: POPUP_RADIUS,
                  border: MED_PANEL_BORDER,
                  boxShadow: 'inset 0 1px 0 rgba(175, 221, 255, 0.14)'
                }}>
                  <Typography  sx={{ color: '#fff', lineHeight: 1.7,fontSize:"12pt",fontWeight:400,fontFamily:"Dubai" }}>
                    {summary.text}
                  </Typography>
                </Box>
              )}

              <Grid container spacing={2}>
                {/* Total Records Card */}
                <Grid item xs={12} sm={4}>
                  <Card sx={{ background: MED_NEUTRAL_CARD_BG, border: MED_NEUTRAL_CARD_BORDER, borderRadius: POPUP_RADIUS, boxShadow: '0 8px 18px rgba(4, 20, 55, 0.16)' }}>
                    <CardContent sx={{ textAlign: 'center', py: 2.2 }}>
                      <Typography  sx={{ color: '#c9d6e1', fontSize:"20pt",fontWeight:500,fontFamily:"Dubai" }}>
                        {medicalRecords.length}
                      </Typography>
                      <Typography  sx={{ color: '#fff', opacity: 0.8 ,fontSize:"12pt",fontWeight:400,fontFamily:"Dubai"}}>
                        Total Records
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Allergies Container */}
                {summary.allergies.length > 0 && (
                  <Grid item xs={12} sm={4}>
                    <Card sx={{ 
                      background: MED_ACCENT_CARD_BG,
                      border: MED_ACCENT_CARD_BORDER,
                      height: '100%',
                      borderRadius: POPUP_RADIUS,
                      boxShadow: '0 8px 20px rgba(4, 24, 60, 0.18)'
                    }}>
                      <CardContent>
                        <Typography variant="subtitle2" sx={{ color: MED_ACCENT_TEXT, fontWeight: 700, mb: 1 }}>
                          ALLERGIES
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {summary.allergies.map((allergy, index) => (
                            <Chip
                              key={index}
                              label={allergy}
                              size="small"
                              sx={{
                                background: MED_ACCENT_CHIP_BG,
                                color: '#dff0ff',
                                 fontFamily:"Dubai",
                                fontWeight: 400,
                                fontSize: '10pt',
                                p:"10px 6px"
                              }}
                            />
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Chronic Diseases Container */}
                <Grid item xs={12} sm={4}>
                  <Card sx={{ 
                    background: MED_ACCENT_CARD_BG,
                    border: MED_ACCENT_CARD_BORDER,
                    height: '100%',
                    borderRadius: POPUP_RADIUS,
                    boxShadow: '0 8px 20px rgba(4, 24, 60, 0.18)'
                  }}>
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ color: MED_ACCENT_TEXT, fontWeight: 700, mb: 1 }}>
                        CHRONIC CONDITIONS
                      </Typography>
                      {summary.chronicDiseases.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {summary.chronicDiseases.map((disease, index) => (
                            <Chip
                              key={index}
                              label={disease}
                              size="small"
                              sx={{
                                background: MED_ACCENT_CHIP_BG,
                                color: '#dff0ff',
                                fontFamily:"Dubai",
                                 fontWeight: 400,
                                fontSize: '10pt',
                                p:"10px 6px"
                              }}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ color: '#fff', opacity: 0.6 }}>
                          None
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ borderColor: 'rgba(107, 165, 232, 0.35)', mb: 3 }} />

            {/* Medical Records List */}
            <Typography  sx={{ color: '#c9d6e1', mb: 1,fontSize:"18pt",fontWeight:500,fontFamily:"Dubai" }}>
              Medical Records
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {medicalRecords.map((record, index) => (
                <Card
                  key={record.id || index}
                  sx={{
                    background: MED_PANEL_BG,
                    border: `1px solid ${getRecordTypeColor(record.type)}36`,
                    borderRadius: POPUP_RADIUS,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: `${getRecordTypeColor(record.type)}90`,
                      boxShadow: `0 10px 22px ${getRecordTypeColor(record.type)}1e`
                    }
                  }}
                >
                  <CardContent>
                    {/* Header with Date and Doctor Info */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box>
                        <Chip
                          label={record.doctorSpecialty || 'Medical Visit'}
                          size="small"
                          sx={{
                            fontFamily:"Dubai",
                            background: `${getRecordTypeColor(record.doctorSpecialty)}30`,
                            color: getRecordTypeColor(record.doctorSpecialty),
                            fontWeight: 600,
                            mb: 1
                          }}
                        />
                        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600,fontFamily:"Dubai" }}>
                          {record.visitReason || 'Medical Record'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mt: 0.5,fontFamily:"Dubai" }}>
                          {record.doctorName} • {record.hospitalName}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'rgba(255, 255, 255, 0.6)', mb: 0.5 }}>
                          <CalendarTodayIcon fontSize="small" />
                          <Typography variant="body2" sx={{fontFamily:"Dubai"}}>
                            {formatDate(record.appointmentDate)}
                          </Typography>
                        </Box>
                        {record.phoneNumber && (
                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)',fontFamily:"Dubai", }}>
                            {record.phoneNumber}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Patient Demographics */}
                    {(record.dateOfBirth || record.gender) && (
                      <Box sx={{ mb: 2, p: 1.5, background: 'rgba(58, 126, 194, 0.08)', borderRadius: POPUP_RADIUS, border: 'none' }}>
                        <Typography variant="caption" sx={{ color: '#fff',fontFamily:"Dubai", fontWeight: 600, display: 'block', mb: 0.5 ,fontSize:"12pt"  }}>
                          Patient Info:
                        </Typography>
                        {record.dateOfBirth && (
                          <Typography variant="body2" sx={{ color: '#ecf2f5',fontFamily:"Dubai",fontSize:"10pt" }}>
                            DOB: {new Date(record.dateOfBirth).toLocaleDateString()} • {record.gender || 'N/A'}
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Lab Results */}
                    {record.labResults && record.labResults !== 'NULL' && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption"  sx={{ color: '#fff', fontWeight: 500, display: 'block', mb: 0.5,fontFamily:"Dubai",fontSize:"14pt"  }}>
                          Lab Results:
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#ecf2f5',fontFamily:"Dubai",fontWeight: 300,fontSize:"12pt" }}>
                          {record.labResults}
                        </Typography>
                      </Box>
                    )}

                    {/* Radiology Report */}
                    {record.radiologyReport && record.radiologyReport !== 'NULL' && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ color: '#fff', fontWeight: 500, display: 'block', mb: 0.5,fontFamily:"Dubai",fontSize:"14pt"  }}>
                          Radiology Report:
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#ecf2f5',fontFamily:"Dubai",fontWeight: 300,fontSize:"12pt" }}>
                          {record.radiologyReport}
                        </Typography>
                      </Box>
                    )}

                    {/* Medications */}
                    {record.medications && record.medications !== 'NULL' && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ color: '#fff', fontWeight: 500, display: 'block', mb: 0.5,fontFamily:"Dubai",fontSize:"14pt"  }}>
                          Medications:
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#ecf2f5',fontFamily:"Dubai",fontWeight: 300,fontSize:"12pt" }}>
                          {record.medications}
                        </Typography>
                      </Box>
                    )}

                    {/* Reports */}
                    {record.reports && record.reports !== 'NULL' && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ color: '#fff', fontWeight: 500, display: 'block', mb: 0.5,fontFamily:"Dubai",fontSize:"14pt"  }}>
                          Reports:
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#ecf2f5',fontFamily:"Dubai",fontWeight: 300,fontSize:"12pt" }}>
                          {record.reports}
                        </Typography>
                      </Box>
                    )}

                    {/* Allergies & Vaccines */}
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      {record.allergies && record.allergies !== 'NULL' && record.allergies !== 'None' && (
                        <Grid item xs={6}>
                          <Box sx={{ p: 1.5, background: 'rgba(70, 132, 206, 0.12)', borderRadius: POPUP_RADIUS, border: 'none' }}>
                            <Typography variant="caption" sx={{ color: '#9ecfff', fontWeight: 600, display: 'block', mb: 0.5,fontFamily:"Dubai" }}>
                              Allergies:
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#fff',fontFamily:"Dubai" }}>
                              {record.allergies}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                      {record.vaccines && record.vaccines !== 'NULL' && (
                        <Grid item xs={6}>
                          <Box sx={{ p: 1.5, background: 'rgba(70, 132, 206, 0.12)', borderRadius: POPUP_RADIUS, border: 'none' }}>
                            <Typography variant="caption" sx={{ color: '#9ecfff', fontWeight: 600, fontFamily:"Dubai" ,display: 'block', mb: 0.5 }}>
                              Vaccines:
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#fff',fontFamily:"Dubai" }}>
                              {record.vaccines}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                    </Grid>

                    {/* Sick Leave */}
                    {record.sickLeave && record.sickLeave !== 'NULL' && (
                      <Box sx={{ mt: 2, pt: 2 }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)',fontFamily:"Dubai" }}>
                          Sick Leave: {record.sickLeave}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MedicalFileDialog;

